import { GridLegacy as Grid, TextField, Button, InputAdornment, IconButton, Box } from '@mui/material';
import { styledTextField } from '@old/styles/generic-styles';
import { useNavigate, useSearchParams } from 'react-router';
import { PATH } from '@old/data/core/paths';
import { useGlobalStore } from '@old/global-store/use-store';
import { useState, useEffect } from 'react';
import { ToastAction } from '@old/ui/toast/toast-action';
import { changeCredentialService } from '@old/services/domain/auth/change-auth-credential';
import { useService } from '@old/hooks/use-service';
import CircularProgress from '@mui/material/CircularProgress';
import i18n from '../../../libs/language';
import { useToggle } from '@old/hooks/core';
import EyeIcon from '@old/assets/svg/core/eye-hollow-green.svg';
import CrossEyeIcon from '@old/assets/svg/core/crossed-eye-green.svg';

export function ResetForm() {
  const { email: storeEmail, setLanguage } = useGlobalStore();
  const [searchParams] = useSearchParams();

  // URL params (set by verify-reset-account on success) take priority over store
  const paramEmail = searchParams.get('email') ?? '';
  const paramLang = searchParams.get('lang');
  const email = paramEmail || storeEmail || '';

  // Apply language from URL param (no API call — public page, no session yet)
  useEffect(() => {
    const lang = paramLang === 'pt' ? 'pt' : paramLang === 'es' ? 'es' : null;
    if (lang) {
      i18n.locale = lang;
      setLanguage(lang);
      localStorage.setItem('LANGUAGE_PREFERENCE', lang);
    }
  }, [paramLang]);
  const [success, setSuccess] = useState(false);

  const PASSWORD_ERROR_MESSAGE = {
    minLength: '',
    maxLength: '',
    lowercase: '',
    uppercase: '',
    number: '',
    specialChar: '',
  };

  const [formReset, setFormReset] = useState({
    password: '',
  });
  const [passwordErrors, setPasswordErrors] = useState(PASSWORD_ERROR_MESSAGE);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useToggle(false);
  const [showConfirmPassword, setConfirmShowPassword] = useToggle(false);

  const checkPasswordValidity = (password: string) => {
    if (password.length < 8)
      setPasswordErrors((prev) => ({
        ...prev,
        minLength: i18n.t('app.public_pages.at_least_eight_characters'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, minLength: '' }));

    if (password.length > 256)
      setPasswordErrors((prev) => ({
        ...prev,
        maxLength: i18n.t('app.public_pages.not_exceed_256_characters'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, maxLength: '' }));

    if (!/[a-z]/.test(password))
      setPasswordErrors((prev) => ({
        ...prev,
        lowercase: i18n.t('app.public_pages.at_least_one_lower_case'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, lowercase: '' }));

    if (!/[A-Z]/.test(password))
      setPasswordErrors((prev) => ({
        ...prev,
        uppercase: i18n.t('app.public_pages.at_least_one_upper_case'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, uppercase: '' }));

    if (!/[0-9]/.test(password))
      setPasswordErrors((prev) => ({ ...prev, number: i18n.t('app.public_pages.at_least_one_number') }));
    else setPasswordErrors((prev) => ({ ...prev, number: '' }));

    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password))
      setPasswordErrors((prev) => ({
        ...prev,
        specialChar: i18n.t('app.public_pages.at_least_one_special_char'),
      }));
    else setPasswordErrors((prev) => ({ ...prev, specialChar: '' }));
  };

  const {
    trigger: resetPassword,
    isError,
    isLoading,
  } = useService(changeCredentialService, {
    onSuccess: () => {
      setSuccess(true);
      setTimeout(() => {
        navigate(`${PATH.LOGIN}?email=${encodeURIComponent(email)}`);
      }, 1500);
    },
  });

  const handleResetPassword = (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    resetPassword({ body: formReset });
  };

  const handleConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputConfirmPassword = e.target.value;
    setConfirmPassword(inputConfirmPassword);
  };

  const navigate = useNavigate();

  const handleTogglePasswordVisibility = () => {
    setShowPassword();
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setConfirmShowPassword();
  };

  const validationAllFields = Object.values(passwordErrors).every((error) => error === '');

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      direction="column"
      spacing={3}
      component="form"
    >
      <Grid item xs={12}>
        <h1 style={{ fontWeight: '300', margin: 0 }}>{i18n.t('app.public_pages.reset_password')}</h1>
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          id="email"
          label={i18n.t('app.public_pages.email')}
          variant="outlined"
          size="small"
          type="email"
          disabled={!!paramEmail}
          defaultValue={email}
          sx={styledTextField}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          id="password"
          type={showPassword ? 'text' : 'password'}
          label={i18n.t('app.public_pages.password')}
          variant="outlined"
          size="small"
          helperText={
            passwordErrors.minLength ||
            passwordErrors.maxLength ||
            passwordErrors.lowercase ||
            passwordErrors.uppercase ||
            passwordErrors.number ||
            passwordErrors.specialChar ? (
              <Box sx={{ color: 'red' }}>{Object.values(passwordErrors).find((c) => c !== '')}</Box>
            ) : undefined
          }
          sx={{ width: '400px' }}
          onChange={(e) => {
            checkPasswordValidity(e.target.value);
            setFormReset({ ...formReset, password: e.target.value.trim() });
          }}
          slotProps={{
            input: {
              style: { background: 'white' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <img src={EyeIcon} /> : <img src={CrossEyeIcon} />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          required
          id="confirm-password"
          type={showConfirmPassword ? 'text' : 'password'}
          label={i18n.t('app.public_pages.password_confirm')}
          variant="outlined"
          color={formReset.password === confirmPassword ? 'primary' : 'error'}
          size="small"
          sx={styledTextField}
          onChange={handleConfirmPassword}
          value={confirmPassword}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleToggleConfirmPasswordVisibility}
                    edge="end"
                  >
                    {showConfirmPassword ? <img src={EyeIcon} /> : <img src={CrossEyeIcon} />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        {!(formReset.password === confirmPassword) && confirmPassword !== '' ? (
          <Box style={{ color: 'red', fontSize: '12px', margin: '0', textAlign: 'right' }}>
            {i18n.t('app.public_pages.passwords_mismatch')}
          </Box>
        ) : null}
      </Grid>
      <Grid item xs={6}>
        <Button
          variant="outlined"
          sx={{ width: '200px', marginRight: '10px' }}
          disableElevation
          onClick={() => {
            navigate(PATH.LOGIN);
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          sx={{ width: '200px', color: 'white' }}
          disableElevation
          onClick={handleResetPassword}
          type="submit"
          disabled={
            !validationAllFields || !(formReset.password === confirmPassword) || confirmPassword === ''
          }
        >
          {isLoading ? <CircularProgress sx={{ marginLeft: '10px', color: 'white' }} size={24} /> : 'Enviar'}
        </Button>
      </Grid>
      {isError && (
        <ToastAction message={i18n.t('app.public_pages.toasts.empty_reset_input')} severity="error" />
      )}
      {success && (
        <ToastAction message={i18n.t('app.public_pages.toasts.success_password_reset')} severity="success" />
      )}
    </Grid>
  );
}
